// 청구서 납부 과정 > 1. 청구서 내용 확인

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { rootStore } from "@/mobx/store";
import styled from "styled-components";
import { observer } from "mobx-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import { Page_settings } from "@/config/page_settings";
import NaverPayIcon from "@/components/icons/naver_pay";
import KakaoPayIcon from "@/components/icons/kakao_pay";
import { ChevronRight } from "@/components/icons/chevron";
import Button from "@/components/button";
import ResetIcon from "@/components/icons/reset";
import AgreementModal from "./AgreementModal";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";
import IconCheck from "@/components/icons/check";
import IconX from "@/components/icons/x";

import CariPayLogoBlack from "@/components/icons/cariPayLogoBlack";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
type PaymentMethod = "CARD" | "NAVER_PAY" | "KAKAO_PAY" | null;

/* **********************************
 * CONSTANTS
 * 상수 정의
 ********************************** */
const PAYMENT_METHODS = [
  { id: "CARD", label: "앱 카드" },
  { id: "NAVER_PAY", label: <NaverPayIcon /> },
  { id: "KAKAO_PAY", label: <KakaoPayIcon /> },
] as const;



const BillConfirmPage = () => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const navigate = useNavigate();
  const context: any = useContext(Page_settings);

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  // 결제 방법 state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);

  // 카드사, 할부 개월 선택 state
  const [selectedCard, setSelectedCard] = useState("");
  const [selectedInstallment, setSelectedInstallment] = useState("");

  // 약관동의 state
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeChewing, setAgreeChewing] = useState(false);
  const [agreePartners, setAgreePartners] = useState(false);

  // 서명 state
  const [showSignatureSheet, setShowSignatureSheet] = useState(false);
  const [signatureData, setSignatureData] = useState<null | {
    data: string;
    width: number;
    height: number;
  }>(null);
  const signatureRef = useRef<SignatureCanvas>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(558);
  const canvasHeight = 180;

  // 약관 동의 state
  const [agreementType, setAgreementType] = useState<
    "service" | "privacy" | null
  >(null);
  // 약관 내용 state
  const [content, setContent] = useState<string>("");

  // 약관동의 바텀시트 state
  const [showAgreementSheet, setShowAgreementSheet] = useState(false);

  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);

  // 버튼 활성화 조건
  const isButtonActive =
    !!paymentMethod &&
    ((paymentMethod === "CARD" &&
      agreeAll &&
      !!selectedCard &&
      !!selectedInstallment) ||
      (paymentMethod !== "CARD" && agreeAll));

  // ref
  const agreementBottomSheetRef = useRef<HTMLDivElement>(null); // 약관동의 바텀시트 ref
  const signatureBottomSheetRef = useRef<HTMLDivElement>(null); // 서명 바텀시트 ref

  /* **********************************
   * HOOKS
   ********************************** */
  useClickOutsideCloseModal([signatureBottomSheetRef], () => {
    // 닫힐 때 현재 서명이 비었으면 signatureData도 제거
    if (!signatureData) {
      setIsSignatureEmpty(true);
    }

    setShowSignatureSheet(false);
  });

  useClickOutsideCloseModal([agreementBottomSheetRef], () => {
    setShowAgreementSheet(false);
  });

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  // 캔버스 width 동적 측정
  useEffect(() => {
    if (
      !showSignatureSheet ||
      !signatureRef.current ||
      !canvasContainerRef.current
    )
      return;

    const container = canvasContainerRef.current;
    const canvas = signatureRef.current.getCanvas();
    const ratio = window.devicePixelRatio || 1;

    // 컨테이너의 실제 크기를 기준으로 캔버스 크기 설정
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // 실제 픽셀 크기 설정
    canvas.width = containerWidth * ratio;
    canvas.height = containerHeight * ratio;

    // CSS 상에서 보이는 크기 유지
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }

    // 기존 서명 복원 (옵션)
    if (signatureData?.data) {
      setTimeout(() => {
        signatureRef.current?.fromDataURL(signatureData.data);
      }, 0);
    }
  }, [showSignatureSheet, signatureData]);

  // 약관 동의 내용 가져오기
  useEffect(() => {
    if (!agreementType) return;

    context.get(`/setting/terms/${agreementType}`, {}, (res: any) => {
      setContent(res);
    });
  }, [agreementType]);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */
  // 모두 동의 핸들러
  const handleToggleAll = () => {
    const newValue = !agreeAll;
    setAgreeAll(newValue);
    setAgreeChewing(newValue);
    setAgreePartners(newValue);
  };

  // 개별 동의 핸들러
  const handleToggleChewing = () => {
    const newValue = !agreeChewing;
    setAgreeChewing(newValue);
    setAgreeAll(newValue && agreePartners);
  };

  const handleTogglePartners = () => {
    const newValue = !agreePartners;
    setAgreePartners(newValue);
    setAgreeAll(newValue && agreeChewing);
  };
  
  
const onFix = () => {

  alert(selectedInstallment);
  alert(paymentMethod);

  const params = new URLSearchParams(window.location.search);
  alert(params);

const userId = params.get('userId'); // "hong123"
alert(userId);

    
  //const location = useLocation(); // ✅ location 객체는 현재 URL 정보를 포함
  // const query = new URLSearchParams(location); // ?userId=hong123
  // const userId = query.get('userId'); // hong123

  // alert('전체 쿼리: ' + navigate.search); // 예: "?userId=hong123"
  // alert('userId: ' + userId);
    // context.post(
    //   `/payment/payTest`,
    //   {
    //     userId: 'admin',
    //     pinned: '222',
    //   },
    //   (response) => {
    //     alert(response.userName);
    //     console.log(response);
    //     //setListSearchStatus((prev) => !prev);
    //   }
    // );
  };

  return (
    <Container>
      <Wrapper>
        {/* 헤더 */}
        <HeaderWrapper>
          <CariPayLogoBlack width="148" height="28" />
        </HeaderWrapper>

        {/* contents */}
        <ContentWrapper>
          {/* 청구 내역 */}
          <ContentBox>
            <Title>청구 내역2</Title>

            <InfoGrid>
              <Row>
                <Label>청구서 발급처</Label>
                <Value>(주)고트헤이븐</Value>
              </Row>
              <Row>
                <Label>수납인</Label>
                <Value>010-1234-5678</Value>
              </Row>
              <Row>
                <Label>청구사유</Label>
                <Value>7월 학원비 납부</Value>
              </Row>
              <Row>
                <Label>안내메세지</Label>
                <Value>
                  안녕하세요. 고트헤이븐 학원입니다. 이번 7월 학원비 납부
                  청구서입니다.
                </Value>
              </Row>
            </InfoGrid>

            <Divider />

            <TotalRow>
              <TotalLabel>총 결제 금액</TotalLabel>
              <TotalValue>270,000원</TotalValue>
            </TotalRow>
          </ContentBox>

          {/* 결제 방법 */}
          <ContentBox>
            <Title>결제 방법</Title>
            <PaymentMethodGrid>
              {PAYMENT_METHODS.map((method) => (
                <PaymentMethodItem
                  key={method.id}
                  $isActive={paymentMethod === method.id}
                  onClick={() => {
                    if (paymentMethod === method.id) return;

                    setPaymentMethod(method.id); // 결제 방법 변경
                    setSelectedCard(""); // 카드사 초기화
                    setSelectedInstallment(""); // 할부 개월 초기화
                    setAgreeAll(false); // 전체 동의 초기화
                    setAgreeChewing(false); // 개별 동의 초기화
                    setAgreePartners(false); // 개별 동의 초기화
                    setSignatureData(null); // 서명 데이터 초기화
                    setIsSignatureEmpty(true); // 서명 데이터 비어있는지 확인 (버튼 disalbed 처리)

                    // 결제 정보 저장
                    rootStore.setPaymentInfo({
                      method: method.id,
                      cardCompany: "",
                      installment: "",
                      signature: null,
                    });
                  }}
                >
                  <PaymentMethodItemTitle>
                    {method.label}
                  </PaymentMethodItemTitle>
                </PaymentMethodItem>
              ))}
            </PaymentMethodGrid>

            {/* 앱카드 선택 시에만 보여줌 */}
            {paymentMethod === "CARD" && (
              <SelectWrapper>
                <NativeSelect
                  value={selectedCard}
                  onChange={(e) => setSelectedCard(e.target.value)}
                  $selected={!!selectedCard}
                >
                  <option value="">카드사 선택</option>
                  <option value="samsung">삼성카드</option>
                  <option value="hyundai">현대카드</option>
                  <option value="kb">국민카드</option>
                  <option value="lotte">롯데카드</option>
                </NativeSelect>

                <NativeSelect
                  value={selectedInstallment}
                  onChange={(e) => setSelectedInstallment(e.target.value)}
                  $selected={!!selectedInstallment}
                >
                  <option value="">할부 개월 선택</option>
                  <option value="lump">일시불</option>
                  <option value="1month">1개월</option>
                  <option value="2months">2개월</option>
                  <option value="3months">3개월</option>
                  <option value="4months">4개월</option>
                  <option value="5months">5개월</option>
                  <option value="6months">6개월</option>
                  <option value="7months">7개월</option>
                  <option value="8months">8개월</option>
                  <option value="9months">9개월</option>
                  <option value="10months">10개월</option>
                  <option value="11months">11개월</option>
                  <option value="12months">12개월</option>
                </NativeSelect>
              </SelectWrapper>
            )}
          </ContentBox>

          {/* 약관 동의 */}
          <ContentBox style={{ padding: "24px 16px" }}>
            <AgreementItem style={{ marginBottom: 16 }}>
              <CheckboxInput checked={agreeAll} onChange={handleToggleAll} />
              <AgreementLabel>모두 동의</AgreementLabel>
            </AgreementItem>

            <AgreementList>
              <AgreementItem>
                <CheckOnlyInput
                  checked={agreeChewing}
                  onChange={handleToggleChewing}
                />
                <AgreementText>
                  [필수] 개인(신용)정보 수집·이용 동의 (카리페이)
                </AgreementText>
                <ChevronRight
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setShowAgreementSheet(true);
                    setAgreementType("privacy");
                  }}
                />
              </AgreementItem>
              <AgreementItem>
                <CheckOnlyInput
                  checked={agreePartners}
                  onChange={handleTogglePartners}
                />
                <AgreementText>
                  [필수] 개인(신용)정보 수집·이용 동의
                  <br />
                  (가맹점, 파트너, 결제중개기관, 금융사)
                </AgreementText>
                <ChevronRight
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setShowAgreementSheet(true);
                    setAgreementType("service");
                  }}
                />
              </AgreementItem>
            </AgreementList>
          </ContentBox>

          {/* 서명 확인 */}
          {signatureData && (
            <SignatureCompleteBox
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <CircleCheckWrapper>
                  <IconCheck color="#007AFF" width="16" height="16" />
                </CircleCheckWrapper>
                <SignatureText>서명 완료되었습니다</SignatureText>
              </div>
              <ChevronRight
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setShowSignatureSheet(true);
                }}
              />
            </SignatureCompleteBox>
          )}
        </ContentWrapper>

        <FooterWrapper>
          <Button
            size="large"
            text="총 270,000원 결제하기"
            width="100%"
            height="48px"
            buttonColor={isButtonActive ? colors.blue600 : colors.gray200}
            textColor={isButtonActive ? colors.white : colors.gray500}
            onClick={() => {
              onFix();
              return;
              if (!paymentMethod) {
                
                alert("결제 방법을 선택해주세요");
                return;
              }
              if (paymentMethod === "CARD") {
                if (!selectedCard || !selectedInstallment || !agreeAll) {
                  alert("청구 내역 확인 및 결제 이용약관에 동의해주세요");
                  return;
                }
              } else {
                if (!agreeAll) {
                  alert("청구 내역 확인 및 결제 이용약관에 동의해주세요");
                  return;
                }
              }
              

              // 서명이 이미 되어 있으면 바로 다음 페이지로
              if (signatureData) {
                navigate("/payments/process");
              } else {
                setShowSignatureSheet(true);
              }
            }}
          />
        </FooterWrapper>

        {showSignatureSheet && (
          <>
            {/* 배경 dimmed 처리 */}
            <DimmedOverlay />
            <SignatureBottomSheet ref={signatureBottomSheetRef}>
              <SheetHeader>
                <span>서명을 해주세요</span>
                <ResetBtn
                  onClick={() => {
                    signatureRef.current?.clear(); // 서명 데이터 초기화
                    setIsSignatureEmpty(true); // 서명 데이터 비어있는지 확인
                    setSignatureData(null); // 서명 데이터 초기화
                  }}
                >
                  <ResetIcon
                    width="20px"
                    height="20px"
                    color={colors.gray500}
                  />
                  <span>다시하기</span>
                </ResetBtn>
              </SheetHeader>

              <CanvasWrapper ref={canvasContainerRef}>
                <SignatureCanvas
                  ref={signatureRef}
                  backgroundColor="#fff"
                  penColor="#1C1C1E"
                  canvasProps={{ width: canvasWidth, height: canvasHeight }}
                  // 서명 완료 후 서명 데이터 비어있는지 확인
                  onEnd={() => {
                    const empty = signatureRef.current?.isEmpty?.() ?? true;
                    setIsSignatureEmpty(empty);
                  }}
                />
              </CanvasWrapper>

              <Button
                size="large"
                text="완료"
                width="100%"
                height="50px"
                buttonColor={colors.blue600}
                textColor={colors.white}
                onClick={() => {
                  const data = signatureRef.current?.toDataURL();
                  if (data) {
                    setSignatureData({
                      data,
                      width: canvasWidth,
                      height: canvasHeight,
                    });
                    setShowSignatureSheet(false);
                  }
                }}
                disabled={isSignatureEmpty}
              />
            </SignatureBottomSheet>
          </>
        )}

        {/* {showAgreementModal && (
            <AgreementModal
              onClose={() => setShowAgreementModal(false)}
              type={agreementType}
            />
          )} */}
        {showAgreementSheet && (
          <>
            {/* 배경 dimmed 처리 */}
            <DimmedOverlay />
            <AgreementBottomSheet ref={agreementBottomSheetRef}>
              <SheetHeader>
                <span></span>
                <IconX
                  color="#333"
                  width="24px"
                  height="24px"
                  onClick={() => setShowAgreementSheet(false)}
                  style={{ cursor: "pointer" }}
                />
              </SheetHeader>
              <Content>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content}
                </ReactMarkdown>
              </Content>
            </AgreementBottomSheet>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default BillConfirmPage;

// 페이지 전체
const Container = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
  padding: 24px 20px;
  box-sizing: border-box;
  margin: 0 auto;
  overflow-y: auto;
`;

// 페이지 전체 > 래퍼
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

// 페이지 전체 > 헤더 래퍼
const HeaderWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

// 페이지 전체 > 컨텐츠 래퍼
const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 90px;
`;

// 컨텐츠 박스
const ContentBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.white};
  padding: 20px 16px;
  border-radius: 12px;
`;

// 컨텐츠 박스 > 서명 완료
const SignatureCompleteBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.white};
  padding: 20px 16px;
  border-radius: 12px;

  animation: slideFadeIn 0.4s cubic-bezier(0.5, 1.4, 0.5, 1);
  transform-origin: top;

  @keyframes slideFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// 컨텐츠 박스 > 청구 내역
const Title = styled.div`
  font-family: ${fonts.body2SemiBold.fontFamily};
  line-height: ${fonts.body2SemiBold.lineHeight};
  font-size: ${fonts.body2SemiBold.fontSize};
  color: ${colors.gray900};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 28px;

  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const Label = styled.div`
  font-size: 14px;
  color: ${colors.gray700};
  white-space: nowrap;
  flex: 0.4;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${colors.gray900};
  text-align: right;
  line-height: 1.6;
  flex: 0.6;
`;

const Divider = styled.div`
  border-top: 1px solid ${colors.gray100};
  margin: 20px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.div`
  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
  color: ${colors.gray700};
`;

const TotalValue = styled.div`
  font-family: ${fonts.body1SemiBold.fontFamily};
  line-height: ${fonts.body1SemiBold.lineHeight};
  font-size: ${fonts.body1SemiBold.fontSize};
  color: ${colors.gray900};
`;

// 컨텐츠 박스 > 결제 방법
const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-top: 32px;
`;

const PaymentMethodItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border: ${(props) => (props.$isActive ? "1.4px solid" : "1px solid")};
  border-color: ${(props) =>
    props.$isActive ? colors.blue600 : colors.gray100};
  background-color: ${(props) =>
    props.$isActive ? colors.blue50 : "transparent"};
  height: 72px;
  border-radius: 8px;
  cursor: pointer;
`;

const PaymentMethodItemTitle = styled.div`
  font-family: ${fonts.body3SemiBold.fontFamily};
  line-height: ${fonts.body3SemiBold.lineHeight};
  font-size: ${fonts.body3SemiBold.fontSize};
  color: ${colors.gray900};
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;

  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: ${fonts.body3Regular.fontSize};

  animation: slideFadeIn 0.4s cubic-bezier(0.5, 1.4, 0.5, 1);
  transform-origin: top;

  @keyframes slideFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const NativeSelect = styled.select<{ $selected?: boolean }>`
  width: 100%;
  padding: 12px 14px;
  padding-right: 36px;
  color: ${colors.gray900};
  background-color: ${({ $selected }) =>
    $selected ? colors.gray50 : colors.white};
  border: ${({ $selected }) => ($selected ? "none" : "1px solid")}
    ${colors.gray100};
  border-radius: 8px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background-image: ${({ $selected }) => {
    const stroke = $selected ? colors.gray800 : colors.gray400;
    const encodedStroke = stroke.replace("#", "%23");
    return `url("data:image/svg+xml;utf8,<svg width='24px' height='24px' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M6 8l4 4 4-4' stroke='${encodedStroke}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`;
  }};

  background-repeat: no-repeat;
  background-position: right 14px center;
  background-position-y: 60%;
  background-size: 24px 24px;

  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: ${fonts.body3Regular.fontSize};

  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
`;

// 약관 동의
const AgreementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Content = styled.article`
  max-width: 1100px;
  line-height: 1.8;
  font-size: 14px;
  font-family: "Noto Sans KR", sans-serif;
  color: #333;
  background: #fff;
  border-radius: 8px;
  padding: 28px 20px;
  height: 100%;
  overflow-y: auto;

  ${({ theme }) => theme.device.mobile} {
    padding: 0;
  }

  h1 {
    font-size: 34px;
    font-weight: 700;
    margin-bottom: 40px;
    color: #1a1a1a;
    border-bottom: 2px solid #eee;
    padding-bottom: 16px;
  }

  h2 {
    font-size: 22px;
    font-weight: 700;
    margin-top: 56px;
    margin-bottom: 28px;
    color: #1a1a1a;
    border-bottom: 1px solid #eee;
    padding-bottom: 12px;
  }

  h3 {
    font-size: 14px;
    font-weight: 600;
    margin-top: 24px;
    margin-bottom: 12px;
    color: #1a1a1a;
  }

  p {
    margin-bottom: 16px;
    color: #4a4a4a;
  }

  ul,
  ol {
    padding-left: 24px;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: 8px;
    color: #4a4a4a;
  }

  strong {
    font-weight: 600;
    color: #1a1a1a;
  }

  a {
    color: #0066cc;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// 약관 동의 > 체크박스
const CheckboxInput = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  background-color: white;
  border: 1px solid ${colors.gray100};
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;

  &::after {
    content: "";
    position: absolute;
    top: 46%;
    left: 46%;
    width: 16px;
    height: 16px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:checked {
    border-color: ${colors.blue600};
    background-color: ${colors.blue600};
  }

  &:checked::after {
    opacity: 1;
    background-image: url("data:image/svg+xml;utf8,<svg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5 11L10 16L17 7' stroke='white' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  }
`;

const CheckOnlyInput = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  display: block;
  line-height: 0;

  &::after {
    content: "";
    display: block;
    width: 24px;
    height: 24px;
    background-repeat: no-repeat;
    background-size: 20px 20px;
    background-position: center;
    opacity: 1;
    transition: background-image 0.2s;
    background-image: url("data:image/svg+xml;utf8,<svg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5 10.5L9 14.5L15 7.5' stroke='%23E5E8EB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  }

  &:checked::after {
    background-image: url("data:image/svg+xml;utf8,<svg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5 10.5L9 14.5L15 7.5' stroke='%23007AFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  }
`;

const AgreementLabel = styled.div`
  font-family: ${fonts.body2SemiBold.fontFamily};
  line-height: ${fonts.body2SemiBold.lineHeight};
  font-size: ${fonts.body2SemiBold.fontSize};
  color: ${colors.gray900};
`;

const AgreementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AgreementText = styled.div`
  flex: 1;
  color: ${colors.gray900};
  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: 14px;
`;

// 페이지 전체 > 푸터 래퍼
const FooterWrapper = styled.div`
  max-width: 600px;
  background-color: ${colors.white};
  width: 100%;
  position: fixed;
  margin: 0 auto;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 16px;
  box-sizing: border-box;
`;

// 약관동의 바텀시트
const AgreementBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  background-color: ${colors.white};
  padding: 28px 20px;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.35s ease-out;
  z-index: 1000;

  height: 85vh;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }

  ${({ theme }) => theme.device.mobile} {
    height: 85vh;
  }
`;

// 서명 바텀시트
const SignatureBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  background-color: ${colors.white};
  padding: 28px 20px 28px 20px;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.35s ease-out;
  z-index: 1000;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.gray900};

  font-family: ${fonts.body1SemiBold.fontFamily};
  line-height: ${fonts.body1SemiBold.lineHeight};
  font-size: ${fonts.body1SemiBold.fontSize};
`;

const ResetBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${colors.gray500};
  cursor: pointer;

  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
`;

const CanvasWrapper = styled.div`
  margin: 20px 0 35px 0;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  height: 180px;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const CircleCheckWrapper = styled.div`
  width: 24px;
  height: 24px;
  background-color: #e9f3ff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SignatureText = styled.div`
  font-family: ${fonts.body2SemiBold.fontFamily};
  line-height: ${fonts.body2SemiBold.lineHeight};
  font-size: 16px;
  color: ${colors.gray900};
`;

// 배경 dimmed 처리
const DimmedOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4); // 적당한 어두움
  z-index: 999; // SignatureBottomSheet보다 낮게
`;
